import { useSelector } from "@tanstack/react-store";
import { useMemo } from "react";

import { Switch } from "@aircall/ds";

import { useEvaluationCreateForm } from "@/hooks/useEvaluationCreateForm/useEvaluationCreateForm";
import {
  EvaluationField,
  EvaluationTextarea,
  PickInputChoices,
} from "../../../EvaluationField";
import {
  buildBinaryRatingScale,
  buildMultipleRatingScale,
  getDescription,
  getHighestPoints,
  getRating,
  hasNotApplicableAnswer,
  hasPossiblyNotApplicable,
} from "../../EvaluationCreateForm.utility";

import {
  BinaryAnswerValue,
  RatingAnswerValue,
} from "@/graphql/generated/graphql";

import { ExcludeNotApplicable } from "@/components/EvaluationResult/EvaluationResult.decl";
import {
  EvaluationFormBinaryQuestionProps,
  EvaluationFormRatingQuestionProps,
} from ".";

export function EvaluationCreateFormRatingQuestion({
  possibleAnswers,
  order,
  text,
  categoryIndex,
  questionIndex,
  isAIEnabled,
  loadingAIQuestions,
  aiAnswersTakingLonger,
  hasAiAnswer,
  onEditQuestion,
}: EvaluationFormRatingQuestionProps) {
  const { form } = useEvaluationCreateForm();

  const fieldQuestion = useSelector(
    form.store,
    (state) => state.values.categories[categoryIndex].questions[questionIndex]
  );

  // Rating scale for the EvaluationField component.
  const fieldRatingScale = useMemo(
    () => buildMultipleRatingScale(possibleAnswers),
    [possibleAnswers]
  );

  const maximumPoints = useMemo(
    () => possibleAnswers.reduce(getHighestPoints, 0),
    [possibleAnswers]
  );

  const isAIAnswerLoading = isAIEnabled && loadingAIQuestions;

  return (
    <EvaluationField<
      Exclude<RatingAnswerValue, RatingAnswerValue.NOT_APPLICABLE>
    >
      mode="multiple"
      rating={
        getRating(fieldQuestion) as Exclude<
          RatingAnswerValue,
          RatingAnswerValue.NOT_APPLICABLE
        >
      }
      notApplicable={hasNotApplicableAnswer(fieldQuestion)}
      possiblyNotApplicable={
        hasPossiblyNotApplicable(fieldQuestion) || isAIEnabled
      }
      description={getDescription(fieldQuestion)}
      comment={fieldQuestion.feedback ?? ""}
      rank={order}
      question={text}
      maximumPoints={maximumPoints}
      ratingScale={fieldRatingScale as Record<RatingAnswerValue, number>}
      hasAiAnswer={hasAiAnswer}
      renderNotApplicableToggle={
        <form.Field
          name={`categories[${categoryIndex}].questions[${questionIndex}].ratingAnswer`}
        >
          {(field) => (
            <Switch
              data-test={`evaluation-na-toggle-c${categoryIndex}-q${questionIndex}`}
              disabled={isAIAnswerLoading}
              checked={field.state.value === "NOT_APPLICABLE"}
              onCheckedChange={(bool) => {
                onEditQuestion?.();
                field.handleChange(
                  bool ? RatingAnswerValue.NOT_APPLICABLE : null
                );
              }}
            />
          )}
        </form.Field>
      }
      renderFeedback={
        <form.Field
          name={`categories[${categoryIndex}].questions[${questionIndex}].feedback`}
        >
          {(field) => (
            <EvaluationTextarea
              dataTest={`evaluation-feedback-c${categoryIndex}-q${questionIndex}`}
              comment={field.state.value ?? ""}
              onChangeComment={(event) => {
                onEditQuestion?.();
                field.handleChange(event.target.value);
              }}
              hasAiAnswer={hasAiAnswer}
              loading={isAIAnswerLoading}
              aiAnswersTakingLonger={aiAnswersTakingLonger}
            />
          )}
        </form.Field>
      }
      renderInputChoices={
        <form.Field
          name={`categories[${categoryIndex}].questions[${questionIndex}].ratingAnswer`}
        >
          {(field) => (
            <PickInputChoices
              mode="multiple"
              value={field.state.value}
              onChange={(value) => {
                onEditQuestion?.();
                field.handleChange(value);
              }}
              disabled={
                field.state.value === "NOT_APPLICABLE" || isAIAnswerLoading
              }
              ratingScale={
                fieldRatingScale as Record<RatingAnswerValue, number>
              }
            />
          )}
        </form.Field>
      }
    />
  );
}

export function EvaluationCreateFormBinaryQuestion({
  possibleAnswers,
  order,
  text,
  categoryIndex,
  questionIndex,
  isAIEnabled,
  loadingAIQuestions,
  aiAnswersTakingLonger,
  hasAiAnswer,
  onEditQuestion,
}: EvaluationFormBinaryQuestionProps) {
  const { form } = useEvaluationCreateForm();

  const fieldQuestion = useSelector(
    form.store,
    (state) => state.values.categories[categoryIndex].questions[questionIndex]
  );

  // Rating scale for the EvaluationField component.
  const fieldRatingScale = useMemo(
    () => buildBinaryRatingScale(possibleAnswers),
    [possibleAnswers]
  );

  const maximumPoints = useMemo(
    () => possibleAnswers.reduce(getHighestPoints, 0),
    [possibleAnswers]
  );

  const isAIAnswerLoading = isAIEnabled && loadingAIQuestions;

  return (
    <EvaluationField<ExcludeNotApplicable<BinaryAnswerValue>>
      mode="binary"
      rating={
        getRating(fieldQuestion) as ExcludeNotApplicable<BinaryAnswerValue>
      }
      /**
       * Special rule for binary question:
       * In the scorecard creation form, a binary question don't have a not applicable option
       * cause we don't want AI to marks it as not applicable, it's a product decision.
       * However, in the evaluation form, we want to allow end users to mark as not applicable as they want,
       * cause some clients ask for this flexibility.
       */
      possiblyNotApplicable={true}
      notApplicable={hasNotApplicableAnswer(fieldQuestion)}
      description={getDescription(fieldQuestion)}
      comment={fieldQuestion.feedback ?? ""}
      rank={order}
      question={text}
      maximumPoints={maximumPoints}
      ratingScale={fieldRatingScale as Record<BinaryAnswerValue, number>}
      hasAiAnswer={hasAiAnswer}
      renderNotApplicableToggle={
        <form.Field
          name={`categories[${categoryIndex}].questions[${questionIndex}].binaryAnswer`}
        >
          {(field) => (
            <Switch
              data-test={`evaluation-na-toggle-c${categoryIndex}-q${questionIndex}`}
              disabled={isAIAnswerLoading}
              checked={field.state.value === BinaryAnswerValue.NOT_APPLICABLE}
              onCheckedChange={(bool) => {
                onEditQuestion?.();
                field.handleChange(
                  bool ? BinaryAnswerValue.NOT_APPLICABLE : null
                );
              }}
            />
          )}
        </form.Field>
      }
      renderFeedback={
        <form.Field
          name={`categories[${categoryIndex}].questions[${questionIndex}].feedback`}
        >
          {(field) => (
            <EvaluationTextarea
              dataTest={`evaluation-feedback-c${categoryIndex}-q${questionIndex}`}
              comment={field.state.value ?? ""}
              onChangeComment={(event) => {
                onEditQuestion?.();
                field.handleChange(event.target.value);
              }}
              hasAiAnswer={hasAiAnswer}
              loading={isAIAnswerLoading}
              aiAnswersTakingLonger={aiAnswersTakingLonger}
            />
          )}
        </form.Field>
      }
      renderInputChoices={
        <form.Field
          name={`categories[${categoryIndex}].questions[${questionIndex}].binaryAnswer`}
        >
          {(field) => (
            <PickInputChoices
              mode="binary"
              value={field.state.value}
              onChange={(value) => {
                onEditQuestion?.();
                field.handleChange(value);
              }}
              disabled={
                field.state.value === "NOT_APPLICABLE" || isAIAnswerLoading
              }
              ratingScale={
                fieldRatingScale as Record<BinaryAnswerValue, number>
              }
            />
          )}
        </form.Field>
      }
    />
  );
}
